import {
	Button,
	Group,
	Modal,
	MultiSelect,
	NumberInput,
	Stack,
	Switch,
	Textarea,
	TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";
import type {
	LicenseFeature,
	LicenseType,
} from "../../store/licenseTypes/licenseTypesSlice";

export interface LicenseTypeFormData {
	key: string;
	name: string;
	description: string;
	monthlyPrice: number;
	features: string[];
	isActive: boolean;
}

interface LicenseTypeModalProps {
	opened: boolean;
	licenseType: LicenseType | null;
	features: LicenseFeature[];
	loading: boolean;
	onClose: () => void;
	onSubmit: (values: LicenseTypeFormData) => void;
}

function LicenseTypeModal({
	opened,
	licenseType,
	features,
	loading,
	onClose,
	onSubmit,
}: LicenseTypeModalProps) {
	const form = useForm<LicenseTypeFormData>({
		initialValues: {
			key: "",
			name: "",
			description: "",
			monthlyPrice: 0,
			features: [],
			isActive: true,
		},
		validate: {
			key: (value) => {
				if (!value) return "Key is required";
				return /^[a-zA-Z][a-zA-Z0-9]*$/.test(value)
					? null
					: "Key must be alphanumeric and start with a letter";
			},
			name: (value) =>
				value.length < 2 ? "Name must have at least 2 characters" : null,
			monthlyPrice: (value) =>
				value < 0 ? "Price cannot be negative" : null,
		},
	});

	useEffect(() => {
		if (licenseType) {
			form.setValues({
				key: licenseType.key,
				name: licenseType.name,
				description: licenseType.description || "",
				monthlyPrice: licenseType.monthlyPrice,
				features: licenseType.features || [],
				isActive: licenseType.isActive,
			});
		} else if (opened) {
			form.reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [licenseType?._id, opened]);

	const handleClose = () => {
		form.reset();
		onClose();
	};

	const featureOptions = features.map((feature) => ({
		value: feature.key,
		label: feature.label,
	}));

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={licenseType ? "Edit License Type" : "Create License Type"}
			size="md">
			<form onSubmit={form.onSubmit(onSubmit)}>
				<Stack gap="md">
					<TextInput
						label="Name"
						placeholder="Presenter"
						required
						{...form.getInputProps("name")}
					/>

					<TextInput
						label="Key"
						placeholder="presenter"
						description="Machine identifier. Cannot be changed once the type exists."
						required
						disabled={!!licenseType}
						{...form.getInputProps("key")}
					/>

					<NumberInput
						label="Monthly Price per License"
						prefix="₪"
						min={0}
						decimalScale={2}
						required
						{...form.getInputProps("monthlyPrice")}
					/>

					<Textarea
						label="Description"
						placeholder="What this license type covers"
						autosize
						minRows={2}
						{...form.getInputProps("description")}
					/>

					<MultiSelect
						label="Features"
						placeholder="Select the capabilities this type grants"
						description="Recorded for future use - nothing enforces these yet."
						data={featureOptions}
						clearable
						{...form.getInputProps("features")}
					/>

					<Switch
						label="Offered on new licenses"
						{...form.getInputProps("isActive", { type: "checkbox" })}
					/>

					<Group
						justify="flex-end"
						mt="lg">
						<Button
							variant="light"
							onClick={handleClose}>
							Cancel
						</Button>
						<Button
							type="submit"
							loading={loading}>
							{licenseType ? "Update" : "Create"}
						</Button>
					</Group>
				</Stack>
			</form>
		</Modal>
	);
}

export default LicenseTypeModal;
